import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_NAVIGATION } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { filter, from, map, switchMap, tap } from 'rxjs';

import { NtyUniversalCreateFeature } from '@ninety/mobile/platform/api';
import { ntyBottomSheetCss } from '@ninety/mobile/platform/components';
import { createToast, UniversalCreateActions, APP_ROUTES } from '@ninety/mobile/platform/state';

import { UniversalCreateComponent } from '../components/universal-create.component';

import { selectCanCreateDiscussion, selectCanViewCompanyAssessments } from './universal-create.selectors';
import { UniversalCreateStore } from './universal-create.store';

@Injectable()
export class UniversalCreateEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly modalCtrl = inject(ModalController);
  private readonly localStore = inject(UniversalCreateStore);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  private readonly modalId = 'nty-universal-create-modal';
  private readonly modalCss = 'nty-universal-create-modal';
  private readonly createRockStore = 'CreateRockStore';
  private readonly createIssueStore = 'CreateIssueStore';

  open$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UniversalCreateActions.open),
        switchMap(() =>
          from(
            this.modalCtrl.create({
              id: this.modalId,
              cssClass: [ntyBottomSheetCss, this.modalCss],
              component: UniversalCreateComponent,
              breakpoints: [0, 0.25, 0.5, 0.75, 1],
              initialBreakpoint: 1,
              handleBehavior: 'none',
            })
          )
        ),
        switchMap(modal => from(modal.present()))
      ),
    { dispatch: false }
  );

  close$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UniversalCreateActions.close),
        tap(({ toastMessage: message }) => this.localStore.toast(createToast({ message }))),
        switchMap(() => from(this.modalCtrl.dismiss(undefined, undefined, this.modalId)))
      ),
    { dispatch: false }
  );

  setDefaultTab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((nav: Nav) => !!nav.payload),
      concatLatestFrom(() => [
        this.store.select(selectCanViewCompanyAssessments),
        this.store.select(selectCanCreateDiscussion),
      ]),
      map(([nav, canViewCompanyAssessments, canCreateDiscussion]) => {
        const url = nav.payload.routerState.url;
        let defaultTab: NtyUniversalCreateFeature = 'issue';
        // Gate each tab on its segment being offered, or the sheet opens a form its own bar omits.
        if (url.includes('to-dos')) defaultTab = 'todo';
        else if (url.includes('discussion') && canCreateDiscussion) defaultTab = 'discussion';
        else if (url.includes('assessment') && canViewCompanyAssessments) defaultTab = 'assessment';
        else if (url.includes('rock')) defaultTab = 'rock';
        else if (url.includes('headline')) defaultTab = 'headline';

        return UniversalCreateActions.setPageValues({ defaultTab });
      })
    )
  );

  //  Refresh my90 stats when issues/rocks are created from the universal create modal
  refreshMyNinetyAfterCreation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UniversalCreateActions.close),
      filter(({ dispatchSource }) => {
        const isRockOrIssueCreation =
          dispatchSource === this.createRockStore || dispatchSource === this.createIssueStore;
        return isRockOrIssueCreation;
      }),
      filter(() => {
        const currentUrl = this.router.url;
        const homeUrl = `/${APP_ROUTES.App}/${APP_ROUTES.Home}`;
        const isOnHome = currentUrl === homeUrl || currentUrl.startsWith(homeUrl);
        return isOnHome;
      }),
      map(() => UniversalCreateActions.refreshMyNinety())
    )
  );
}

interface Nav {
  payload: { routerState: { url: string } };
}
